import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, Users2, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { TEAM_STAT_LABELS, TEAM_STAT_KEYS } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export function ModTeam() {
  const { lang } = useLang();
  const [stats, setStats] = useState(TEAM_STAT_KEYS.map((k) => ({ key: k, value: "" })));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => {
      const byKey = {};
      (r.data.stats || []).forEach((s) => (byKey[s.key] = s.value));
      setStats(TEAM_STAT_KEYS.map((k) => ({ key: k, value: byKey[k] || "" })));
      setMembers(r.data.members || []);
    }).finally(() => setLoading(false));
  }, []);

  const setStat = (key, value) => setStats((s) => s.map((x) => (x.key === key ? { ...x, value } : x)));
  const setMember = (i, field, value) => setMembers((m) => m.map((x, j) => (j === i ? { ...x, [field]: value } : x)));
  const addMember = () => setMembers((m) => [...m, { name: "", role: "", note: "" }]);
  const removeMember = (i) => setMembers((m) => m.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/mod/team", { stats, members });
      toast.success("Mentve");
    } catch { toast.error("Hiba"); } finally { setSaving(false); }
  };

  if (loading) return <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="mod-team">
      <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-5">
        <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-zinc-100">
          <Users2 size={16} className="text-blue-400" /> Statisztikák
        </div>
        <p className="mb-4 text-[12px] text-zinc-500">A főoldali "Csapatunk" blokkban jelennek meg. Az üres mezők nem látszanak. Felirat: „Automatikusan frissül 3 naponta" (az értékeket Ön adja meg).</p>
        <div className="space-y-2">
          {stats.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-3 rounded-md border border-zinc-800/70 px-4 py-2.5">
              <span className="text-[13px] text-zinc-300">{TEAM_STAT_LABELS[s.key]?.[lang] || TEAM_STAT_LABELS[s.key]?.en}</span>
              <Input
                data-testid={`team-stat-${s.key}`}
                value={s.value}
                onChange={(e) => setStat(s.key, e.target.value)}
                placeholder="pl. 1 240"
                className="h-9 w-40 border-zinc-800 bg-zinc-950 text-[13px]"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-zinc-100">
            <UserRound size={16} className="text-blue-400" /> Csapattagok
          </div>
          <Button data-testid="team-add-member" onClick={addMember} variant="outline" className="h-8 gap-1 border-zinc-800 bg-zinc-900 text-[12px] text-zinc-300 hover:bg-zinc-800">
            <Plus size={14} /> Tag hozzáadása
          </Button>
        </div>
        {members.length === 0 && <p className="text-[12px] text-zinc-600">Még nincs csapattag hozzáadva.</p>}
        <div className="space-y-3">
          {members.map((m, i) => (
            <div key={i} className="rounded-md border border-zinc-800/70 p-4" data-testid={`team-member-${i}`}>
              <div className="grid gap-2 sm:grid-cols-2">
                <Input data-testid={`team-member-name-${i}`} value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} placeholder="Név (opcionális)" className="h-9 border-zinc-800 bg-zinc-950 text-[13px]" />
                <Input data-testid={`team-member-role-${i}`} value={m.role} onChange={(e) => setMember(i, "role", e.target.value)} placeholder="Szerepkör (pl. Ex-tartalommoderátor)" className="h-9 border-zinc-800 bg-zinc-950 text-[13px]" />
              </div>
              <Textarea data-testid={`team-member-note-${i}`} value={m.note} onChange={(e) => setMember(i, "note", e.target.value)} placeholder="Rövid leírás" className="mt-2 min-h-[56px] resize-none border-zinc-800 bg-zinc-950 text-[13px]" />
              <button data-testid={`team-member-remove-${i}`} onClick={() => removeMember(i)} className="mt-2 inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-red-400">
                <Trash2 size={13} /> Eltávolítás
              </button>
            </div>
          ))}
        </div>
      </div>

      <Button data-testid="team-save" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Mentés
      </Button>
    </div>
  );
}
